import classNames from "classnames";
import { VerticalDivider } from "components/design/VerticalDivider";
import { HelpCalender } from "components/icons/Calender";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { HelpCall } from "components/icons/HelpCall";
import { InkleBooksHelp } from "components/icons/Logos/InkleBooksHelp";
import { InkleTaxHelp } from "components/icons/Logos/InkleTaxHelp";
import { Online } from "components/icons/Online";
import {
  ANAND_GENERIC_MEET_LINK,
  BOOKKEEPING_CONSULTATION,
  INKLE_CONSULTATION,
  TAX_CALENDLY_LINK,
} from "constants/meetingRequestUrls";
import { useModal } from "hooks/useModal";
import { useDispatch } from "react-redux";
import { useGetEntitiesQuery } from "store/apis/group";
import { openMeetWithTranqModal } from "store/slices/chat";
import Anand from "../../../src/static/images/anand.png";
import ToolTip from "components/design/toolTip";
import { SupportTeam } from "components/icons/LeftNav/SupportTeam";
import {
  SUPPORT_EXECUTIVE,
  SUPPORT_TEAM,
  TAX_PREPARER,
  BOOKKEEPING_EXPERT,
  CEO,
} from "constants/help";

export type HelpCardProps = {
  type: string;
  showSchedule?: boolean;
  showCall?: boolean;
  isOnline: boolean;
  profileImage?: string;
  name?: string;
  showDivider?: boolean;
  availableIn?: string;
  showAvailableIn?: boolean;
  setOpen: (open: boolean) => void;
  agentCallingModals?: ReturnType<typeof useModal>;
  setModalType?: () => void;
  isDisabled?: boolean;
  disabledCall?: () => void;
  showCallTooltip?: boolean;
  showDisabledCall?: boolean;
  isInTimeRange?: boolean;
  showIcon?: boolean;
  actionOrientation?: "horizontal" | "vertical";
  customTooltipMessage?: string;
};

type GetProfileImageProps = Pick<
  HelpCardProps,
  "type" | "profileImage" | "isDisabled"
>;

type ProfileProps = Pick<HelpCardProps, "type" | "profileImage" | "isDisabled">;

type ScheduleAndCallSectionProps = Pick<
  HelpCardProps,
  | "showCall"
  | "isDisabled"
  | "type"
  | "disabledCall"
  | "showCallTooltip"
  | "name"
  | "showSchedule"
  | "showDisabledCall"
  | "actionOrientation"
> & {
  handleSchedule: () => void;
  handleCall: () => void;
  customTooltipMessage?: string;
};

const SupportTeamProfile = ({ isOnline }: { isOnline: boolean }) => {
  return (
    <div className="t-flex t-items-end">
      <SupportTeam className="t-h-11 t-w-11" />
      {isOnline && <Online className="-t-ml-3.5 -t-mt-3 t-text-green" />}
    </div>
  );
};

const GetProfileImage = ({
  type,
  profileImage,
  isDisabled = false,
}: GetProfileImageProps) => {
  switch (type) {
    case SUPPORT_EXECUTIVE:
      if (!profileImage) {
        return <div className="t-text-purple-50 t-text-subtext">SE</div>;
      }
      return (
        <img
          className="t-w-10 t-h-10 t-rounded-full"
          src={profileImage}
          alt={type}
        />
      );
    case TAX_PREPARER:
      return (
        <InkleTaxHelp
          className={classNames({
            "t-text-neutral-20": isDisabled,
            "t-text-neutral-40": !isDisabled,
          })}
        />
      );

    case BOOKKEEPING_EXPERT:
      return (
        <InkleBooksHelp
          className={classNames({
            "t-text-neutral-20": isDisabled,
            "t-text-neutral-40": !isDisabled,
          })}
        />
      );
    case CEO:
      return (
        <img className="t-w-10 t-h-10 t-rounded-full" src={Anand} alt="anand" />
      );
    default:
      return (
        <>
          {Boolean(profileImage) ? (
            <img
              src={profileImage}
              alt="Profile"
              className="t-w-10 t-h-10 t-rounded-full"
            />
          ) : (
            <div className="t-w-10 t-h-10 t-flex t-justify-center t-items-center t-rounded-full t-text-subtext t-text-purple-50 t-bg-purple-0">
              ST
            </div>
          )}
        </>
      );
  }
};

const Profile = ({ type, profileImage, isDisabled }: ProfileProps) => {
  return (
    <div className="t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-[200px]  t-bg-neutral-0">
      <GetProfileImage
        profileImage={profileImage}
        type={type}
        isDisabled={isDisabled}
      />
    </div>
  );
};

const ScheduleAndCallSection = ({
  showCall,
  handleSchedule,
  handleCall,
  isDisabled,
  disabledCall,
  showCallTooltip,
  name,
  type,
  showSchedule,
  showDisabledCall,
  actionOrientation,
  customTooltipMessage,
}: ScheduleAndCallSectionProps) => {
  const isVerticalView = actionOrientation === "vertical";

  const tooltipForAssignedManager = `${
    name ? name : type
  } is offline right now, Leave a message on chat and we’ll reply within 24 hours`;

  const disabledCallTooltip = customTooltipMessage ?? tooltipForAssignedManager;

  return (
    <div
      className={classNames("t-flex t-mt-1", {
        "t-flex-col t-items-start t-gap-1": isVerticalView,
        "t-flex-row t-items-center t-gap-3": !isVerticalView,
      })}
    >
      {isDisabled ? (
        <div
          onClick={disabledCall}
          className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer"
        >
          <div>
            <DiamondPurple height={12} width={12} />
          </div>
          <div>Upgrade for access</div>
        </div>
      ) : (
        <>
          {showSchedule && (
            <div
              className="t-flex t-gap-1 t-text-purple t-text-button-sm t-items-center t-cursor-pointer"
              onClick={handleSchedule}
            >
              <HelpCalender />
              Schedule
            </div>
          )}
          {showCall && showSchedule && !isVerticalView && (
            <VerticalDivider size="xsmall" />
          )}
          {!showCallTooltip && showCall && (
            <div
              className={classNames(
                "t-flex t-gap-1 t-text-button-sm t-items-center t-cursor-pointer",
                {
                  "t-text-purple": !showDisabledCall,
                  "t-text-neutral": showDisabledCall,
                }
              )}
              onClick={!showDisabledCall ? handleCall : undefined}
            >
              <HelpCall className="t-size-3" />
              Call
            </div>
          )}
          {showCallTooltip && (
            <ToolTip
              text={
                <div className="!t-max-w-[288px]">{disabledCallTooltip}</div>
              }
            >
              <div className="t-flex t-gap-1 t-text-neutral t-text-button-sm t-items-center">
                <HelpCall className="t-size-3" />
                Call
              </div>
            </ToolTip>
          )}
        </>
      )}
    </div>
  );
};

export const HelpCard = ({
  type,
  showSchedule = true,
  showCall,
  isOnline,
  profileImage,
  name,
  showDivider,
  availableIn,
  showAvailableIn = false,
  setOpen,
  agentCallingModals,
  setModalType,
  isDisabled,
  disabledCall,
  showCallTooltip,
  showDisabledCall,
  isInTimeRange,
  showIcon = true,
  actionOrientation = "horizontal",
  customTooltipMessage,
}: HelpCardProps) => {
  const { data: { groups: [group] = [null] } = {} } = useGetEntitiesQuery();
  const dispatch = useDispatch();

  const isSupportTeam = type === SUPPORT_TEAM;

  const handleSchedule = () => {
    setOpen(false);
    switch (type) {
      case TAX_PREPARER:
        dispatch(openMeetWithTranqModal(TAX_CALENDLY_LINK));
        break;
      case BOOKKEEPING_EXPERT:
        dispatch(openMeetWithTranqModal(BOOKKEEPING_CONSULTATION));
        break;
      case SUPPORT_EXECUTIVE:
      case SUPPORT_TEAM:
        dispatch(openMeetWithTranqModal(INKLE_CONSULTATION));
        break;
      case CEO:
        dispatch(openMeetWithTranqModal(ANAND_GENERIC_MEET_LINK));
        break;
      default:
        break;
    }
  };

  const handleCall = () => {
    setModalType?.();
    setOpen(false);
    agentCallingModals?.open();
  };

  const availableInTime = showAvailableIn ? availableIn : "";

  return (
    <section className="t-rounded-lg t-flex t-flex-col t-h-full t-bg-surface-lighter-grey">
      <div className="t-flex t-items-start t-gap-4 t-p-3">
        {isSupportTeam ? (
          <div className="t-w-10 t-h-10">
            <SupportTeamProfile isOnline={isOnline} />
          </div>
        ) : (
          <div className="t-flex t-justify-end t-items-end">
            <Profile
              type={type}
              profileImage={profileImage}
              isDisabled={isDisabled}
            />
            {showIcon && isOnline && isInTimeRange && (
              <Online className="-t-ml-3 t-h-[14] t-w-[14] t-text-green" />
            )}
          </div>
        )}
        <div className="t-flex t-flex-col">
          <div
            className={classNames("t-text-subtext-sm", {
              "t-text-text-30": isDisabled,
              "t-text-text-60": !isDisabled,
            })}
          >
            {type}
          </div>
          {name && (
            <div
              className={classNames("t-text-body-sm-lite t-pt-1", {
                "t-text-neutral-40": isDisabled,
                "t-text-text-30": !isDisabled,
              })}
            >
              {name}
            </div>
          )}
          {availableInTime && (
            <div className="t-text-caption t-text-text-30 t-mt-1 t-italic">
              {availableInTime}
            </div>
          )}
          <ScheduleAndCallSection
            handleSchedule={handleSchedule}
            showCall={showCall}
            handleCall={handleCall}
            isDisabled={isDisabled}
            type={type}
            disabledCall={disabledCall}
            showCallTooltip={showCallTooltip}
            name={name}
            showSchedule={showSchedule}
            showDisabledCall={showDisabledCall}
            actionOrientation={actionOrientation}
            customTooltipMessage={customTooltipMessage}
          />
        </div>
      </div>
    </section>
  );
};
